import React from 'react';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

/** 実行結果表示用の正規化データ（実行直後/履歴クリック共通） */
export type ExecutionResultView = {
  seq?: number;
  statusId: number;
  statusDescription: string;
  stdout: string | null;
  stderr: string | null;
  compileOutput: string | null;
  message: string | null;
  time: number | null;
  wallTime: number | null;
  memory: number | null;
  exitCode: number | null;
  exitSignal: number | null;
  /** 実行時に使用したコマンドライン引数（半角スペース区切り。引数なしは null） */
  arguments: string | null;
};

type Props = {
  open: boolean;
  result: ExecutionResultView | null;
  /** 実行直後(run) か 履歴クリック(history) か。タイトル表示の出し分けに使う */
  mode?: 'run' | 'history';
  onClose: () => void;
};

type StatusKind = 'success' | 'error' | 'warning';

/** status.id が Accepted(3) なら success、TLE/内部エラーは warning、それ以外は error */
const statusKind = (statusId: number): StatusKind => {
  if (statusId === 3) return 'success';
  if (statusId === 5 || statusId === 13) return 'warning';
  return 'error';
};

/** ステータス種別に対応するテーマトークン色（`success.main` 等） */
const STATUS_COLOR: Record<StatusKind, string> = {
  success: 'success.main',
  error: 'error.main',
  warning: 'warning.main',
};

const StatusIcon: React.FC<{ kind: StatusKind }> = ({ kind }) => {
  const sx = { fontSize: 22, color: STATUS_COLOR[kind] };
  if (kind === 'success') return <CheckCircleOutlineIcon sx={sx} />;
  if (kind === 'warning') return <WarningAmberIcon sx={sx} />;
  return <ErrorOutlineIcon sx={sx} />;
};

/** メトリクスの1セル（ラベル小・値大）。カード状に並べる */
const MetricCell: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box
    sx={{
      flex: '1 1 0',
      minWidth: 88,
      px: 1.5,
      py: 1,
      borderRadius: 2,
      border: 1,
      borderColor: 'divider',
      bgcolor: 'action.hover',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.25 }}>
      {label}
    </Typography>
    <Typography variant="body2" fontWeight={600} sx={{ fontFamily: 'monospace' }}>
      {value}
    </Typography>
  </Box>
);

/**
 * 出力ブロック（ターミナル風カード）。
 * 上部にヘッダーバー（信号機ドット + ラベル）、下に黒背景のプロンプト風本文。
 * dotColor でウィンドウ左上ドットの主色を出力種別ごとに変える。
 */
const OutputBlock: React.FC<{
  label: string;
  value: string | null;
  dotColor?: string;
}> = ({ label, value, dotColor = '#e6edf3' }) => {
  if (!value) return null;
  return (
    <Box
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: 1,
        borderColor: '#2d333b',
        bgcolor: '#0d1117',
      }}
    >
      {/* ターミナルのタイトルバー */}
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ px: 1.5, py: 0.75, bgcolor: '#161b22', borderBottom: 1, borderColor: '#2d333b' }}
      >
        <Stack direction="row" spacing={0.5}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: dotColor }} />
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3d444d' }} />
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#3d444d' }} />
        </Stack>
        <Typography
          variant="caption"
          fontWeight={600}
          sx={{ color: '#adbac7', letterSpacing: '0.02em' }}
        >
          {label}
        </Typography>
      </Stack>
      <Box
        component="pre"
        sx={{
          m: 0,
          p: 1.5,
          bgcolor: '#0d1117',
          color: '#e6edf3',
          fontSize: '0.8rem',
          fontFamily: 'monospace',
          lineHeight: 1.6,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          overflowX: 'auto',
        }}
      >
        {value}
      </Box>
    </Box>
  );
};

/**
 * 実行結果詳細モーダル（実行直後 / 履歴クリック共通）。
 */
const ExecutionResultModal: React.FC<Props> = ({ open, result, mode = 'history', onClose }) => {
  const hasOutput = Boolean(
    result && (result.stdout || result.stderr || result.compileOutput || result.message),
  );
  const kind = result ? statusKind(result.statusId) : 'error';
  const subtitle =
    mode === 'run'
      ? '実行が完了しました'
      : result?.seq != null
        ? `履歴 #${result.seq}`
        : '実行結果';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="lg"
      slotProps={{
        paper: {
          elevation: 0,
          sx: {
            borderRadius: 3,
            border: 1,
            borderColor: 'divider',
            maxHeight: '85vh',
          },
        },
      }}
    >
      {/* ヘッダー: ステータスアイコン + タイトル/サブタイトル + 閉じる */}
      <DialogTitle sx={{ p: 2.5, pb: 2 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={1.5}>
            {result && (
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  bgcolor: 'action.hover',
                }}
              >
                <StatusIcon kind={kind} />
              </Box>
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight={700} sx={{ lineHeight: 1.2 }}>
                {result?.statusDescription ?? '実行結果'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {subtitle}
                {result != null && ` ・ status id=${result.statusId}`}
              </Typography>
            </Box>
          </Stack>
          <IconButton size="small" onClick={onClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers sx={{ overflow: 'auto', p: 2.5 }}>
        {result && (
          <Stack spacing={2.5}>
            {/* メトリクス（カード状に横並び） */}
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              <MetricCell label="実行時間" value={result.time != null ? `${result.time}s` : '-'} />
              <MetricCell
                label="実測時間"
                value={result.wallTime != null ? `${result.wallTime}s` : '-'}
              />
              <MetricCell
                label="メモリ"
                value={result.memory != null ? `${result.memory}KB` : '-'}
              />
              <MetricCell
                label="終了コード"
                value={result.exitCode != null ? String(result.exitCode) : '-'}
              />
              <MetricCell
                label="終了シグナル"
                value={result.exitSignal != null ? String(result.exitSignal) : '-'}
              />
            </Stack>

            {/* 実行時引数（引数なしは「（なし）」） */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                実行時引数
              </Typography>
              {result.arguments ? (
                <Box
                  component="code"
                  sx={{
                    px: 1,
                    py: 0.25,
                    borderRadius: 1.5,
                    bgcolor: 'action.hover',
                    border: 1,
                    borderColor: 'divider',
                    fontFamily: 'monospace',
                    fontSize: '0.8rem',
                  }}
                >
                  {result.arguments}
                </Box>
              ) : (
                <Typography variant="caption" color="text.secondary">
                  （なし）
                </Typography>
              )}
            </Stack>

            {/* 出力（ターミナル風・種別で左上ドットの色分け） */}
            <OutputBlock label="標準出力 (stdout)" value={result.stdout} dotColor="#3fb950" />
            <OutputBlock label="標準エラー (stderr)" value={result.stderr} dotColor="#f85149" />
            <OutputBlock
              label="コンパイル出力 (compile output)"
              value={result.compileOutput}
              dotColor="#d29922"
            />
            <OutputBlock label="メッセージ (message)" value={result.message} dotColor="#58a6ff" />
            {!hasOutput && (
              <Box
                sx={{
                  py: 3,
                  textAlign: 'center',
                  borderRadius: 2,
                  border: 1,
                  borderColor: 'divider',
                  borderStyle: 'dashed',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  出力はありません
                </Typography>
              </Box>
            )}
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ExecutionResultModal;
